import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { useMarkets } from '../../hooks/useMarkets';
import { Market } from '../../types/market';
import { MarketCard } from './MarketCard';
import { SEO } from '../seo/SEO';

export const MarketsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMarketId, setExpandedMarketId] = useState<string | null>(null);
  const { markets, marketSearches, isLoading } = useMarkets();

  const filteredMarkets = markets.filter(market =>
    market.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    market.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getMarketSearches = (marketId: string) => {
    return marketSearches.filter(item => item.marketId === marketId);
  };

  const handleMarketClick = (market: Market) => {
    setExpandedMarketId(expandedMarketId === market.id ? null : market.id);
  };

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-theme(spacing.32))] flex items-center justify-center px-4">
        <div className="w-full max-w-7xl">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg w-full max-w-2xl mx-auto"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Betting Markets & Predictions | Banga.ai"
        description="Explore our AI-powered betting markets for soccer matches. Get expert predictions and analysis across multiple betting categories including match winners, goals, and more."
        canonicalUrl="https://banga.ai/markets"
      />
      
      <div className="min-h-[calc(100vh-theme(spacing.32))] flex items-center justify-center px-4">
        <div className="w-full max-w-7xl">
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search markets..."
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              />
            </div>
          </div>

          {/* Markets Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMarkets.map((market, index) => {
              const isExpanded = expandedMarketId === market.id;
              return (
                <div 
                  key={market.id}
                  className={`relative ${isExpanded ? 'z-20' : 'z-10'}`}
                  style={{
                    gridRow: isExpanded ? 'span 2' : 'auto'
                  }}
                >
                  <MarketCard
                    market={market}
                    searches={getMarketSearches(market.id)}
                    isExpanded={isExpanded}
                    onToggle={() => handleMarketClick(market)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};